import {Injectable} from '@angular/core';
import * as UIModels from '@ui-app/models';
import {CountryEnum} from '@shared-models/common.models';
import {UserRoleEnum} from '@shared-models/common.enums';

@Injectable({
    providedIn: 'root'
})
export class UserRuleReader {
    isUSUser = false;
    isUSIUser = false;
    canVisitorsRequest = false;
    canBadgeRequest = false;

    user: UIModels.CM.IUserData | null;

    constructor() {
    }

    /** Returns all effective role IDs — supports both multi-role and single-role users. */
    private get effectiveRoleIds(): number[] {
        if (!this.user) { return []; }
        return this.user.userRoleIds?.length ? this.user.userRoleIds : [this.user.userRoleId];
    }

    /** Returns true if the current user has at least one of the given role IDs. */
    hasAnyRole(...roleIds: number[]): boolean {
        return this.effectiveRoleIds.some(id => roleIds.includes(id));
    }

    /** Maps a single base role ID to its region-specific variant. */
    private mapRoleByRegion(roleId: number, isUSUser: boolean, isUSIUser: boolean): number {
        if (roleId === UserRoleEnum.Csba && isUSUser)             { return UserRoleEnum.CsbaUS; }
        if (roleId === UserRoleEnum.Csba && isUSIUser)            { return UserRoleEnum.CsbaUSI; }
        if (roleId === UserRoleEnum.Security && isUSUser)         { return UserRoleEnum.SecurityUS; }
        if (roleId === UserRoleEnum.Security && isUSIUser)        { return UserRoleEnum.SecurityUSI; }
        if (roleId === UserRoleEnum.Facilitator && isUSIUser)     { return UserRoleEnum.FacilitatorUSI; }
        if (roleId === UserRoleEnum.SeniorManager && isUSIUser)   { return UserRoleEnum.SeniorManagerUSI; }
        if (roleId === UserRoleEnum.FrontDesk && isUSIUser)       { return UserRoleEnum.FrontDeskUSI; }
        if (roleId === UserRoleEnum.Employee && isUSIUser)        { return UserRoleEnum.EmployeeUSI; }
        return roleId;
    }

    // Add US Role
    modifyUserRole(user: UIModels.CM.IUserData): UIModels.CM.IUserData {
        this.isUSUser = user.country === CountryEnum.US;
        this.isUSIUser = user.country === CountryEnum.USI;

        user.userRoleId = this.mapRoleByRegion(user.userRoleId, this.isUSUser, this.isUSIUser);

        if (user.userRoleIds?.length) {
            user.userRoleIds = user.userRoleIds.map(id => this.mapRoleByRegion(id, this.isUSUser, this.isUSIUser));
        }

        this.user = user;
        this.user.isUSI = this.isUSIUser;
        return user;
    }

    // Add US Role
    modifyUserRoleByCountry(roleId: number, country: string): number {
        this.isUSUser = country === CountryEnum.US;
        this.isUSIUser = country === CountryEnum.USI;
        return this.mapRoleByRegion(roleId, this.isUSUser, this.isUSIUser);
    }

    checkByRole(rule: IUserRule): boolean {
        if (!this.user) { return false; }
        const isSeniorManagerAllowed = rule.roles?.includes(UserRoleEnum.SeniorManagerUSI) && this.user?.isSeniorManagerAndAbove;
        const roleIds = this.effectiveRoleIds;
        if (rule.exclude) {
            return (!roleIds.some(id => rule.roles.includes(id)) || !isSeniorManagerAllowed);
        } else {
            return roleIds.some(id => rule.roles.includes(id)) || isSeniorManagerAllowed;
        }
    }

    checkByCountry(rule: IUserRule): boolean {
        if (rule.exclude) {
            return this.user ? !rule.countries.includes(this.user.country) : false;
        } else {
            return this.user ? rule.countries.includes(this.user.country) : false;
        }
    }


}

export interface IUserRule {
    roles?: number[];
    exclude?: boolean;
    countries?: string[];
}

export interface IUserRules {
    [key: string]: IUserRule;
}
